'use client'
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { X } from "lucide-react";
import useCommonStore from "@/store/commonStore";

interface Location {
    lat: number
    lng: number
}

interface LocationMapProps {
    open: boolean
    onClose: () => void
    onConfirm: (doc:Record<string, any>,center:Location) => void
    initialLat: number
    initialLng: number
    locationString?: string
}

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAP_BOX_KEY||""

export default function LocationMap({ open, onClose, onConfirm,initialLng,initialLat,locationString }: LocationMapProps) {
        if (!open) return null
    const initialZoom = 15;
    const {translations} = useCommonStore()


    const mapContainer = useRef(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);

    const [center, setCenter] = useState({ lng: initialLng, lat: initialLat });
    const [address, setAddress] = useState("");
    const [rawAddress, setRawAddress] = useState<Record<string, any>>({});
    const [searchQuery, setSearchQuery] = useState(locationString || "");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [showResults, setShowResults] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Forward geocode function (search location)
    async function searchLocation(query: string) {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxgl.accessToken}&limit=5`;
        const res = await fetch(url);
        const data = await res.json();

        if (data && data.features) {
            setSearchResults(data.features);
            setShowResults(true);
        }
    }

    // Debounced search
    function handleSearchChange(value: string) {
        setSearchQuery(value);
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        if (!value.trim()) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            searchLocation(value);
        }, 500);
    }

    // Select a search result
    async function selectLocation(feature: any) {
        const [lng, lat] = feature.center;
        if (mapRef.current) {
            mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
        }
        setSearchQuery(feature.place_name);
        setShowResults(false);
        setCenter({ lng, lat });
        await reverseGeocode(lng, lat);
    }

    // Reverse geocode function
    async function reverseGeocode(lng: number, lat: number) {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`;
        const res = await fetch(url);
        const data = await res.json();
        if(data){
            setRawAddress(data);
        }
        if (data && data.features && data.features[0]) {
            setAddress(data.features[0].place_name);
        } else {
            setAddress("No address found");
        }
    }

    useEffect(() => {
        if (!mapRef.current && mapContainer.current) {
            mapRef.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/mapbox/streets-v11",
                center: [initialLng, initialLat],
                zoom: initialZoom,
            });

            // Initial reverse geocode
            reverseGeocode(initialLng, initialLat);

            // Listen for map move end
            mapRef.current.on("moveend", () => {
                if (mapRef.current) {
                    const lngLat = mapRef.current.getCenter();
                    setCenter({ lng: lngLat.lng, lat: lngLat.lat });
                    reverseGeocode(lngLat.lng, lngLat.lat);
                }
            });
        }

        return () => {
            if (mapRef.current) mapRef.current.remove();
        };
    }, []);


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative w-full md:max-w-2xl bg-white rounded-none md:rounded-2xl md:shadow-lg overflow-hidden h-full md:max-h-[90dvh]">
                <div className="flex items-center justify-between p-4 md:p-6 shadow md:shadow-none bg-white">
                    <h3 className="text-lg font-bold">{translations['set_location'] || 'Set Location'}</h3>
                    <button onClick={onClose} aria-label="Close" className="text-gray-500"><X/></button>
                </div>

                {/* Search bar */}
                <div className="px-4 pb-4 md:px-6 md:pb-6 bg-white border-b relative">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onFocus={() => searchResults.length > 0 && setShowResults(true)}
                        placeholder={translations['search_location'] || 'Search for a location...'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D401A]"
                    />
                    
                    {/* Search results dropdown */}
                    {showResults && searchResults.length > 0 && (
                        <div className="absolute top-full left-4 right-4 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-30">
                            {searchResults.map((result, index) => (
                                <button
                                    key={index}
                                    onClick={() => selectLocation(result)}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                                >
                                    <div className="font-medium text-sm">{result.text}</div>
                                    <div className="text-xs text-gray-500">{result.place_name}</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="h-[calc(100dvh-279px)] md:h-[calc(100dvh-386px)] bg-gray-100 relative">
                    <div ref={mapContainer} className="w-full h-full">
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none z-20">
                        <svg width="40" height="48" viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 0C8.95 0 0 8.95 0 20C0 35 20 48 20 48C20 48 40 35 40 20C40 8.95 31.05 0 20 0ZM20 27C16.13 27 13 23.87 13 20C13 16.13 16.13 13 20 13C23.87 13 27 16.13 27 20C27 23.87 23.87 27 20 27Z" fill="#6D401A" />
                        </svg>
                    </div>
                </div>

                <div className="flex flex-col gap-4 p-4 md:p-6 bg-white">
                    <div className="">
                        <p className="font-bold">Address:</p>
                        <p className="text-sm line-clamp-2">{address}</p>
                    </div>
                    <button
                        className="w-full px-3 py-3 rounded bg-[#6D401A] w-1/2 text-white text-base font-bold"
                        onClick={() => onConfirm(rawAddress,center)}
                    >
                        {translations['continue'] || 'Continue'}
                    </button>
                </div>
            </div>
        </div>
    )
}